'use client';

import { useMemo, useState } from 'react';
import { convertVttToSrt, countCues } from '@/lib/browser-subtitle-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

function downloadText(value: string, fileName: string, mimeType: string) {
  const blob = new Blob([value], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const SAMPLE_VTT = `WEBVTT

NOTE This header block is dropped on conversion.

intro
00:00:01.000 --> 00:00:04.000 align:start
The quick brown fox

00:00:04.500 --> 00:00:07.200
jumps over the lazy dog.`;

export default function BrowserVttToSrt() {
  const [input, setInput] = useState<string>(SAMPLE_VTT);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const output = useMemo(() => (input.trim() ? convertVttToSrt(input) : ''), [input]);
  const cueCount = useMemo(() => (output ? countCues(output) : 0), [output]);
  const hasInput = input.trim().length > 0;
  const noCues = hasInput && cueCount === 0;

  async function handleCopy() {
    try {
      await copyText(output);
      setStatusMessage('Copied SubRip to clipboard.');
    } catch {
      setStatusMessage('The browser could not copy the value.');
    }
  }

  function handleDownload() {
    downloadText(output, 'subtitles.srt', 'application/x-subrip');
    setStatusMessage('Downloaded subtitles.srt.');
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Client-side</span>
        <h2 className="tool-live-title">VTT to SRT converter</h2>
        <p className="tool-live-subtitle">
          Paste WebVTT (.vtt) subtitles and get SubRip (.srt) with the header removed, cues
          renumbered, and timestamps fixed. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>WebVTT (.vtt) input</span>
              <textarea
                onChange={(event) => setInput(event.target.value)}
                rows={16}
                value={input}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!output}
              onClick={handleCopy}
              type="button"
            >
              Copy result
            </button>
            <button
              className="button button-secondary"
              disabled={!output}
              onClick={handleDownload}
              type="button"
            >
              Download .srt
            </button>
          </div>
          {noCues ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              No subtitle cues were found. Check that the input is valid WebVTT (.vtt) text.
            </p>
          ) : null}
          {statusMessage ? <p className="status-message status-success">{statusMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>SubRip (.srt) output — {cueCount} cues</span>
            <textarea readOnly rows={16} value={output} />
          </label>
          <p className="tool-live-panel-note">Converted locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
